import {
  familyForTenant,
  migrateLegacyStaffTenantPrefs,
  readStaffTenantSlug,
  roleCanAccess,
  subscribeStaffTenantChange,
} from "@l30/shared";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { useCurrentRole } from "../../api/auth.js";
import { useTenants } from "../../api/tenants.js";
import { applyStaffTenantSwitch } from "../../lib/staff-tenant-switch.js";

export function StaffTenantSelect() {
  const role = useCurrentRole();
  const canList = roleCanAccess("tenants:list", role);
  const tenantsQuery = useTenants({ enabled: canList });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [slug, setSlug] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    migrateLegacyStaffTenantPrefs();
    return readStaffTenantSlug();
  });

  useEffect(() => {
    const sync = () => setSlug(readStaffTenantSlug());
    return subscribeStaffTenantChange(sync);
  }, []);

  const tenantSummaries = useMemo(() => tenantsQuery.data?.tenants ?? [], [tenantsQuery.data]);

  if (!canList) return null;

  return (
    <label className="flex min-w-0 items-center gap-2 text-xs font-medium text-fg-subtle">
      <span className="hidden sm:inline">Tenant</span>
      <select
        aria-label="Active tenant"
        className="max-w-xs rounded-soft-md border border-border bg-surface px-2 py-1.5 text-sm text-fg shadow-rest"
        value={slug ?? ""}
        disabled={tenantsQuery.isLoading || !tenantsQuery.data}
        onChange={(e) => {
          const next = e.target.value || null;
          if (next === slug) return;
          void applyStaffTenantSwitch({
            nextSlug: next,
            navigate: (opts) => navigate(opts),
            queryClient,
          });
        }}
      >
        <option value="">Host default</option>
        {tenantSummaries.map((tenant) => (
          <option key={tenant.slug} value={tenant.slug}>
            {tenant.slug} ({familyForTenant(tenant.slug, tenant.portalFamily)})
          </option>
        ))}
      </select>
    </label>
  );
}

/** Demo path: Documents, Atlas, Ask Insights — labelled header links, same tenant as the chrome. */
const DEMO_PATH_LINK =
  "inline-flex items-center rounded-soft-md border border-border bg-surface px-3 py-1.5 text-sm text-fg transition-colors hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand";

export function StaffDemoPath() {
  return (
    <nav aria-label="Knowledge path" className="hidden items-center gap-2 md:flex">
      <Link to="/documents" className={DEMO_PATH_LINK}>
        Documents
      </Link>
      <Link to="/ideation/maps" className={DEMO_PATH_LINK}>
        Atlas
      </Link>
      <Link to="/ai" className={DEMO_PATH_LINK}>
        Ask Insights
      </Link>
    </nav>
  );
}
